import {
  CalendarOutlined,
  LoadingOutlined,
  SearchOutlined
} from "@ant-design/icons"
import { Dropdown, Flex, Spin, Typography } from "antd"
import type { ItemType } from "antd/es/menu/interface"
import React, {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react"

import useDebounce from "../../../../../hooks/use-debounce"
import { searchEvents } from "../../../api/services.ts"
import { type SearchEventItem } from "../../../api/services.types.ts"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

const MIN_SEARCH_LENGTH = 3
const DEBOUNCE_DELAY = 500

interface CalendarFiltersSearchProps {
  value: string
  onChange: (value: string) => void
  onSelectEvent: (id: string) => void
}

export function CalendarFiltersSearch({
  value,
  onChange,
  onSelectEvent
}: CalendarFiltersSearchProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [events, setEvents] = useState<SearchEventItem[]>([])

  const debouncedSearchValue = useDebounce(value, DEBOUNCE_DELAY)

  const isValidSearch = useMemo(
    () => debouncedSearchValue?.trim()?.length >= MIN_SEARCH_LENGTH,
    [debouncedSearchValue]
  )

  const performSearch = useCallback(async (q: string) => {
    setIsLoading(true)
    try {
      const response = await searchEvents({ q })
      if (!response?.ok) throw new Error("")

      setEvents(response?.data?.events || [])
    } catch (error) {
      console.error("Search error:", error)
      setEvents([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleItemClick = useCallback(
    (item: SearchEventItem) => {
      setIsDropdownOpen(false)
      onChange("")
      setEvents([])
      onSelectEvent(item?.id)
    },
    [onChange, onSelectEvent]
  )

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      onChange(newValue)

      // Показуємо індикатор завантаження одразу при введенні
      if (newValue.trim().length >= MIN_SEARCH_LENGTH) {
        setIsLoading(true)
        setIsDropdownOpen(true)
      } else if (newValue.trim().length > 0) {
        setIsDropdownOpen(true)
      }
    },
    [onChange]
  )

  const eventMenuItems: ItemType = useMemo(() => {
    if (!events?.length) return null

    return {
      type: "group",
      label: `Found (${events.length})`,
      children: events?.map((event: SearchEventItem) => ({
        key: `event-${event?.id}`,
        className: "overflow-hidden block",
        label: (
          <Flex gap={"small"} align={"center"}>
            <CalendarOutlined />
            <Typography.Paragraph
              className={"mb-0"}
              ellipsis={{ rows: 1, tooltip: event?.title }}
            >
              {event?.title}
            </Typography.Paragraph>
          </Flex>
        ),
        onClick: () => handleItemClick(event)
      }))
    }
  }, [events, handleItemClick])

  const menuItems = useMemo((): ItemType[] => {
    const items = []
    const trimmedValue = value.trim()

    // Показуємо підказку про мінімальну довжину
    if (trimmedValue.length > 0 && trimmedValue.length < MIN_SEARCH_LENGTH) {
      return [
        {
          key: "min-length",
          label: (
            <div className="text-center py-3 text-gray-500">
              <div className="text-sm">
                Enter at least {MIN_SEARCH_LENGTH - trimmedValue.length} more
                character
                {MIN_SEARCH_LENGTH - trimmedValue.length > 1 ? "s" : ""} to
                search
              </div>
            </div>
          ),
          disabled: true
        }
      ]
    }

    // Показуємо індикатор пошуку
    if (isLoading && trimmedValue.length >= MIN_SEARCH_LENGTH) {
      return [
        {
          key: "loading",
          label: (
            <div className="flex items-center justify-center py-4">
              <Spin indicator={<LoadingOutlined spin />} size="small" />
              <span className="ml-2 text-gray-500">Searching...</span>
            </div>
          ),
          disabled: true
        }
      ]
    }

    // Показуємо результати пошуку
    if (eventMenuItems) {
      items.push(eventMenuItems)
    }

    // Показуємо повідомлення про відсутність результатів
    if (
      isValidSearch &&
      !isLoading &&
      events.length === 0 &&
      debouncedSearchValue === trimmedValue
    ) {
      items.push({
        key: "no-results",
        label: (
          <div className="text-center py-4 text-gray-500">
            <SearchOutlined className="text-2xl mb-2 text-gray-400" />
            <div className="font-medium">No events found</div>
            <div className="text-sm mt-1">
              Try searching with different keywords
            </div>
          </div>
        ),
        disabled: true
      })
    }

    return items
  }, [
    value,
    debouncedSearchValue,
    isLoading,
    isValidSearch,
    events,
    eventMenuItems
  ])

  useEffect(() => {
    if (isValidSearch) {
      performSearch(debouncedSearchValue)
    } else {
      setEvents([])
      setIsLoading(false)
    }
  }, [debouncedSearchValue, isValidSearch, performSearch])

  return (
    <Dropdown
      menu={{ items: menuItems }}
      open={isDropdownOpen && menuItems.length > 0}
      onOpenChange={setIsDropdownOpen}
      trigger={["click"]}
      placement="bottomLeft"
      className={"w-auto xl:min-w-[220px]"}
      overlayClassName="calendar-search-overlay max-w-0"
      popupRender={(menu) => (
        <div className="bg-white rounded-lg shadow-xl border border-gray-200">
          <div className="max-h-96 overflow-auto">{menu}</div>
        </div>
      )}
    >
      <TextInput disableAutoCapitalize
        className={"2xl:min-w-72"}
        prefix={<SearchOutlined />}
        suffix={
          isLoading && value.trim().length >= MIN_SEARCH_LENGTH ? (
            <Spin indicator={<LoadingOutlined spin />} size="small" />
          ) : null
        }
        placeholder={"Search by title, lead or project"}
        value={value}
        onChange={handleSearchChange}
        allowClear={!isLoading}
      />
    </Dropdown>
  )
}
